import api from '@/lib/axiosInstance'
import { areaReading } from './area'
import type { ListingOnGenerate } from '@/lib/agentSettings'
import type { HeldFile, IntakeDraft } from './aiIntake'

/**
 * Creates the listing from a reviewed draft, the way the eight-step form
 * creates it: the same `POST /listings` with the same fields, then the held
 * photos to `POST /listings/:id/medias`.
 *
 * The review only lets this run once every field the form insists on is
 * filled, so nothing is guessed here. Anything the review does not cover
 * (facilities, unit features, floor details) is simply not sent, exactly as
 * it would be if the agent left those steps untouched.
 */
interface CreateArgs {
    draftIndex: number
    draft: IntakeDraft
    media: HeldFile[]
    proof: File | null
    /** The conversation it came from, for the AI badge on the listing. */
    sessionPublicId?: string | null
    /** What the agent asked for in Settings: draft, approval queue, or live. */
    onGenerate?: ListingOnGenerate
    /** Whether the owner is asked to confirm the listing. */
    requireOwnerValidation?: boolean
}

const str = (v: unknown) => (v === null || v === undefined ? '' : String(v))
const flag = (v: unknown) => (v === 'Yes' || v === true || v === 1 ? '1' : '0')

export async function createListingFromDraft({ draft, draftIndex, media, proof, sessionPublicId, onGenerate = 'draft', requireOwnerValidation = false }: CreateArgs): Promise<number> {
    const f = draft.fields
    const form = new FormData()

    // Location. The address came from Google unless the agent chose to
    // type it by hand, and the pin is approximate with a radius.
    form.append('manualAddress', f.manualAddress === 1 ? '1' : '0')
    form.append('rawAddress', str(f.rawAddress))
    form.append('stateId', str(f.stateId))
    form.append('cityId', str(f.cityId))
    form.append('township', str(f.township))
    form.append('noTownship', f.township ? '0' : '1')
    form.append('street', str(f.street))
    form.append('unit', str(f.unitNumber))
    form.append('unitNumber', str(f.unitNumber))
    form.append('postcode', str(f.postcode))
    form.append('useTrueLocation', '0')
    form.append('lat', str(f.lat))
    form.append('lng', str(f.lng))
    form.append('radius', str(f.radius))

    // Land details.
    form.append('selectedDistrict', str(f.districtId))
    form.append('selectedSubdistrict', str(f.subdistrictId))
    // The form sends the title type's row ID under this name.
    form.append('selectedTitleTypeCode', str(f.landTitleTypeId))
    form.append('jupemNo', str(f.jupemNo))
    form.append('ownershipNumber', str(f.ownershipNumber))
    // Square feet, as a plain number: the listing page reads it as one.
    form.append('sizeOfLand', str(areaReading(f, 'sizeOfLand').sqft))
    form.append('lotNo', str(f.lotNo))
    form.append('markAsDuplicate', 'false')
    if (proof) form.append('proof', proof)

    // What kind of listing this is.
    form.append('listingTypeId', str(f.listingTypeId ?? draft.category.ids.listingTypeId))
    form.append('projectTypeId', str(f.projectTypeId ?? draft.category.ids.projectTypeId))
    form.append('forSaleOrRentId', str(f.forSaleOrRentId ?? draft.category.ids.forSaleOrRentId))
    form.append('rentalTypeId', str(f.rentalTypeId))
    form.append('leaseTermId', '')
    form.append('availableDate', '')
    form.append('listingReferenceNumber', '')
    form.append('coAgencyListing', flag(f.coAgencyListing))
    form.append('coShareListing', flag(f.coShareListing))
    form.append('forAuction', flag(f.forAuction))
    form.append('forTender', flag(f.forTender))
    form.append('coShareAgents', '[]')
    form.append('coAgencyAgents', '[]')

    // The property itself.
    form.append('propertyTypeId', str(f.propertyTypeId ?? draft.category.ids.propertyTypeId))
    form.append('propertySubTypeId', str(f.propertySubTypeId ?? draft.category.ids.propertySubTypeId))
    form.append('propertyUnitTypeId', str(f.propertyUnitTypeId ?? draft.category.ids.propertyUnitTypeId))
    form.append('zoningId', str(f.zoningId))
    form.append('tenureId', str(f.tenureId ?? draft.category.ids.tenureId))
    form.append('titleTypeId', str(f.titleTypeId ?? draft.category.ids.titleTypeId))
    form.append('directionId', '')
    form.append('yearOfCompletion', str(f.yearOfCompletion))
    form.append('landTypeTitleId', '')
    form.append('isBumiLot', f.bumiLot === 'Yes' ? '1' : f.bumiLot === 'No' ? '0' : '')

    // Size and rooms.
    form.append('noOfBathrooms', str(f.noOfBathrooms))
    form.append('noOfBedrooms', str(f.noOfBedrooms))
    form.append('noOfMaidRooms', str(f.noOfMaidrooms))
    form.append('noOfParkingSpots', str(f.noOfParkingSpots))
    // Whatever unit the review showed, the listing is sent square feet.
    const builtUp = areaReading(f, 'builtUp').sqft
    const landArea = areaReading(f, 'landArea').sqft
    form.append('builtUp', str(builtUp))
    form.append('builtUpUnit', builtUp ? 'sqft' : '')
    form.append('landArea', str(landArea))
    form.append('landAreaUnit', landArea ? 'sqft' : '')
    form.append('furnishingConditionId', str(f.furnishingConditionId ?? draft.category.ids.furnishingConditionId))

    // Price and copy.
    form.append('price', str(draft.pricing.price ?? f.price))
    form.append('maintenanceFee', str(f.maintenanceFee))
    form.append('headline', str(f.headline))
    form.append('description', str(f.description))

    // The badge on the listing, and the conversation it came from.
    form.append('createChannel', 'ai')
    form.append('aiDraftIndex', String(draftIndex))
    if (draft.draftId) form.append('aiDraftId', draft.draftId)
    if (sessionPublicId) form.append('aiSessionId', sessionPublicId)

    const { data } = await api.post('/listings', form)
    if (!data.status) throw new Error(data.error)
    const listingId = Number(data.listing.id)
    if (data.alreadyCreated) return listingId

    // The photos were held in the browser until there was a listing to hang
    // them on. A failure here leaves the listing; the caller says so.
    const images = media.filter((m) => m.kind === 'image')
    const videos = media.filter((m) => m.kind === 'video')
    for (const [mediaType, group] of [[1, images], [3, videos]] as const) {
        if (!group.length) continue
        const mediaForm = new FormData()
        mediaForm.append('type', String(mediaType))
        group.forEach((m) => mediaForm.append('files', m.file))
        const { data: mediaData } = await api.post(`/listings/${listingId}/medias`, mediaForm, { headers: { 'Content-Type': 'multipart/form-data' } })
        if (!mediaData.status) throw new MediaError(listingId, mediaData.error)
    }
    // Settings can carry it further than the Draft tab. The listing already
    // exists, so a failure here is reported without losing it.
    if (onGenerate !== 'draft') {
        const onward = new FormData()
        for (const [key, value] of form.entries()) onward.append(key, value)
        onward.append('owners', '[]')
        // The API reads this as the fork: asked-to-validate goes for approval,
        // not-asked goes live.
        onward.append('requireOwnerValidation', onGenerate === 'submit' || requireOwnerValidation ? '1' : '0')
        const { data: after } = await api.post(`/listings/${listingId}`, onward)
        if (!after.status) throw new StageError(listingId, after.error)
    }
    return listingId
}

/** The listing was created but could not be sent onward from Drafts. */
export class StageError extends Error {
    listingId: number

    constructor(listingId: number, message: string) {
        super(message)
        this.name = 'StageError'
        this.listingId = listingId
    }
}

/** The listing exists but its gallery did not upload. */
export class MediaError extends Error {
    listingId: number

    constructor(listingId: number, message: string) {
        super(message)
        this.name = 'MediaError'
        this.listingId = listingId
    }
}
